import { useContext, forwardRef } from 'react'
import cx from 'classnames'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import Styles from './RadioGroup.module.scss'
import { useObjectRef } from 'utils/useObjectRef'
import { useRadio } from '@react-aria/radio'
import { useFocusRing } from '@react-aria/focus'

import { RadioContext } from './RadioContext'
import { RadioProps, Variant } from './Types'
import { RadioGroupState } from '@react-stately/radio'
import { useHover } from '@react-aria/interactions'
import { RadioBullet } from './RadioBullet'

const Radio = forwardRef<HTMLInputElement, RadioProps>(({ children, ...props }, fRef) => {
  const { className, classNames, variant = Variant.GREEN, rightAligned } = props
  const state = useContext(RadioContext) as RadioGroupState
  const ref = useObjectRef(fRef)
  const { isFocusVisible, focusProps } = useFocusRing()
  const isSelected = state.selectedValue === props.value
  const isDisabled = props.isDisabled
  const { hideRadio = false, size = 16 } = props
  const { hoverProps, isHovered } = useHover(props)
  const actualChildren = typeof children === 'function' ? children({ isFocusVisible, isSelected, isHovered }) : children
  const { inputProps } = useRadio(
    {
      ...props,
      children: actualChildren,
    },
    state,
    ref,
  )

  const radioHandleProps = {
    size,
    isSelected,
    isFocusVisible,
  }

  return (
    <label
      className={cx(Styles.radioLabel, className, Styles[variant], {
        [Styles.disabled]: isDisabled,
        [classNames?.hovered || '']: isHovered,
        [classNames?.selected || '']: isSelected,
        [classNames?.focused || '']: isFocusVisible,
      })}
      {...hoverProps}
    >
      <VisuallyHidden>
        <input {...inputProps} {...focusProps} ref={ref} />
      </VisuallyHidden>
      {!hideRadio && !rightAligned && <RadioBullet {...radioHandleProps} rightAligned={false} />}
      {actualChildren}
      {!hideRadio && rightAligned && <RadioBullet {...radioHandleProps} rightAligned />}
    </label>
  )
})

export { Radio }
